import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useFlyoutDispatch, useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { secondaryEmailsMutations } from 'src/queries/secondaryEmails/secondaryEmails.queries'
import { createSetIsAddSecondaryEmailPopupOpen } from 'src/redux/actions/app.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { parseBackendErrors } from 'src/utils/error.utils'
import {
  stringIsEmpty,
  stringIsValidEmail,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type SecondaryEmailFlatFields = {
  email?: string
}

export type SecondaryEmailVerificationFlatFields = {
  verificationCode?: string
}

interface AddSecondaryEmailFormProps {
  // Called with the added email so the popup can advance to the verify step.
  // Replaces the legacy ADD_SECONDARY_EMAIL_SUCCESS -> selectedSecondaryEmail
  // Redux write.
  onAdded: (secondaryEmail: string) => void
}

const AddSecondaryEmailForm: React.FC<AddSecondaryEmailFormProps> = (props) => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)

  const addSecondaryEmailMutation = useMutation(secondaryEmailsMutations.add())
  // Intentional fix: the button now shows an in-flight spinner while POST
  // /useremails is pending. The legacy `secondaryEmailLoading` flag was a no-op
  // (the reducer initialised it false and never set it true), so the old button
  // never spun. `isPending` is the correct mapping.
  const loading = addSecondaryEmailMutation.isPending
  const topLevelError = addSecondaryEmailMutation.error
    ? parseBackendErrors(addSecondaryEmailMutation.error).topLevelError
    : undefined

  const { analytics } = useAnalyticsContext()
  const dispatch = useFlyoutDispatch()

  const {
    fields: secondaryEmailFlatFields,
    fieldsErrors: secondaryEmailFlatFieldsErrors,
    onFieldChange: onSecondaryEmailFlatFieldChange,
    setErrorsAndReturnDidPassValidation: secondaryEmailFlatFieldsSetErrors,
  } = useFormFields<SecondaryEmailFlatFields>(
    {},
    () => ({
      email: [stringIsEmpty(), stringIsValidEmail()],
    }),
    undefined,
  )

  const sendVerificationCode = (): void => {
    if (secondaryEmailFlatFieldsSetErrors() && secondaryEmailFlatFields.email) {
      analytics.track('secondary email verification - verify')
      addSecondaryEmailMutation.mutate(
        { secondaryEmail: secondaryEmailFlatFields.email },
        {
          onSuccess: (data) => {
            props.onAdded(data.secondaryEmail)
          },
        },
      )
    }
  }

  return (
    <>
      <div
        css={css`
          margin-bottom: 40px;
        `}
      >
        <TextInputField
          fieldLabel={translate('Secondary Email')}
          placeholder={translateSingleString(locale, 'Enter email address')}
          design={TextInputFieldDesigns.New}
          onChange={(val: string): void =>
            onSecondaryEmailFlatFieldChange('email', val)
          }
          type={'email'}
          autoComplete={'email'}
          errors={secondaryEmailFlatFieldsErrors['email']}
        />
        {topLevelError && (
          <ErrorList
            errors={[topLevelError]}
            css={css`
              margin-bottom: 8px;
            `}
          />
        )}
      </div>
      <div
        css={css`
          display: flex;
          gap: 20px;
          align-items: center;
        `}
      >
        <Button
          type={ButtonTypes.button}
          design={ButtonDesigns.designCta}
          big={true}
          text={translate('Send Verification Code')}
          onClick={sendVerificationCode}
          loading={loading}
        />
        <HoverableLink
          underline
          ariaLabelSource={'Cancel'}
          onClick={(): void => {
            analytics.track('secondary email verification - cancel')
            dispatch(createSetIsAddSecondaryEmailPopupOpen(false))
          }}
        >
          {translate('Cancel')}
        </HoverableLink>
      </div>
    </>
  )
}

export default AddSecondaryEmailForm
